import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'core-system/Modal'
import TextField from 'core-system/TextField'
import Text from 'core-system/Text'
import Dropdown from 'core-system/Dropdown'
import { SelectItemProps } from 'core-system/Dropdown/Select'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import { SwitchToggleV2 } from 'core-system/SwitchToggle'
import WorksiteGroupDropdown from 'features/Programs/Launch/Components/WorksiteGroupDropdown'
import { SegmentListType } from 'features/Programs/Shared/types'
import pxToRem from 'core-system/utils/pxToRem'
import palette from 'core-system/Themes/palette'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import { automatedReportingActions } from 'redux/automatedReporting/automatedReportingSlice'
import {
  ScheduledReport,
  ScheduledReportCycle,
  ScheduledReportMonth,
  ScheduledReportType,
} from 'redux/automatedReporting/automatedReportingTypes'
import {
  buildScheduledReportPayload,
  getInitialScheduledReportForm,
  getScheduledReportFormValidation,
  getScheduledReportSegmentItems,
  getScheduledReportSelectedSegments,
  initialScheduledReportForm,
  ScheduledReportFormField,
  scheduledReportCycleItems,
  scheduledReportMonthItems,
  scheduledReportTypeItems,
} from '../utils'

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: ${pxToRem(420)};
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.5rem;
`

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const RangeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`

const Label = styled(Text)`
  font-weight: 300;
  margin-bottom: 0.75rem;
`

const SectionDescription = styled(Text)`
  color: ${palette.text.secondary};
`

const HelpText = styled(Text)`
  color: ${palette.error.red500};
  margin-top: 0.5rem;
`

const FooterContent = styled.div`
  display: flex;
  gap: 0.75rem;
`

const ActivationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

interface ScheduledReportCreateModalProps {
  open: boolean
  onClose: () => void
  scheduledReport?: ScheduledReport | null
}

const ScheduledReportCreateModal = React.memo(
  ({ open, onClose, scheduledReport }: ScheduledReportCreateModalProps) => {
    const dispatch = useDispatch()
    const { allSegments } = useSelector((state: AppState) => state.employer)
    const {
      scheduledReportCreateLoading,
      scheduledReportCreateError,
      scheduledReportCreateSuccess,
      scheduledReportUpdateLoading,
      scheduledReportUpdateError,
      scheduledReportUpdateSuccess,
    } = useSelector((state: AppState) => state.automatedReporting)
    const [form, setForm] = useState(initialScheduledReportForm)
    const [selectedSegments, setSelectedSegments] = useState<SegmentListType[]>(
      []
    )
    const [submitted, setSubmitted] = useState(false)
    const isEditing = Boolean(scheduledReport)

    useEffect(() => {
      if (open) {
        setForm(getInitialScheduledReportForm(scheduledReport))
        setSelectedSegments(
          getScheduledReportSelectedSegments(allSegments || [], scheduledReport)
        )
        setSubmitted(false)
        dispatch(automatedReportingActions.resetScheduledReportCreateState())
        dispatch(automatedReportingActions.resetScheduledReportUpdateState())
      }
    }, [allSegments, dispatch, open, scheduledReport])

    useEffect(() => {
      if (scheduledReportCreateSuccess || scheduledReportUpdateSuccess) {
        onClose()
        dispatch(automatedReportingActions.resetScheduledReportCreateState())
        dispatch(automatedReportingActions.resetScheduledReportUpdateState())
      }
    }, [
      dispatch,
      onClose,
      scheduledReportCreateSuccess,
      scheduledReportUpdateSuccess,
    ])

    const isSaving =
      scheduledReportCreateLoading || scheduledReportUpdateLoading
    const saveError = isEditing
      ? scheduledReportUpdateError
      : scheduledReportCreateError
    const {
      isNameInvalid,
      isDateInvalid,
      isStartDayInvalid,
      isEndDayInvalid,
      isRangeInvalid,
      areRecipientsInvalid,
      isSubmitDisabled,
    } = getScheduledReportFormValidation(form, isSaving)
    const showNameInvalid = submitted && isNameInvalid
    const showDateInvalid = submitted && isDateInvalid
    const showStartDayInvalid = submitted && isStartDayInvalid
    const showEndDayInvalid = submitted && isEndDayInvalid
    const showRangeInvalid = submitted && isRangeInvalid

    const updateForm = (
      field: ScheduledReportFormField,
      value: string | boolean
    ) => {
      setForm((currentForm) => ({
        ...currentForm,
        [field]: value,
      }))
    }

    const closeModal = () => {
      dispatch(automatedReportingActions.resetScheduledReportCreateState())
      dispatch(automatedReportingActions.resetScheduledReportUpdateState())
      onClose()
    }

    const submit = () => {
      setSubmitted(true)
      if (isSubmitDisabled) {
        return
      }

      const payload = buildScheduledReportPayload(
        form,
        selectedSegments,
        isEditing
      )

      if (scheduledReport) {
        dispatch(
          automatedReportingActions.updateScheduledReport({
            ...scheduledReport,
            ...payload,
          })
        )
        return
      }

      dispatch(automatedReportingActions.createScheduledReport(payload))
    }

    return (
      <Modal open={open} onClose={closeModal} width={pxToRem(560)}>
        <ModalHeader
          title={
            isEditing ? 'Update Scheduled Report' : 'Create Scheduled Report'
          }
          onClose={closeModal}
        />
        <ModalBody padding='1.5rem 3.25rem'>
          <Form>
            <TextField
              label='Report Name*'
              value={form.name}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                updateForm('name', event.currentTarget.value)
              }
              invalid={showNameInvalid}
              helpText='This is a required field.'
              width='100%'
            />
            <Field>
              <Label variant='action2'>Report Type*</Label>
              <Dropdown
                items={scheduledReportTypeItems}
                active={form.reportType}
                itemCallback={(item: SelectItemProps) =>
                  updateForm('reportType', item.id as ScheduledReportType)
                }
                width='100%'
              />
            </Field>
            <Field>
              <Label variant='action2'>Cycle*</Label>
              <Dropdown
                items={scheduledReportCycleItems}
                active={form.cycle}
                itemCallback={(item: SelectItemProps) =>
                  updateForm('cycle', item.id as ScheduledReportCycle)
                }
                width='100%'
              />
            </Field>
            <TextField
              label='Next Report Date*'
              type='date'
              value={form.nextReportDate}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                updateForm('nextReportDate', event.currentTarget.value)
              }
              invalid={showDateInvalid}
              helpText='This is a required field.'
              width='100%'
            />
            <Section>
              <SectionHeader>
                <Text variant='h5'>Report Range</Text>
                <SectionDescription variant='body2'>
                  Choose the start and end of the data window relative to each
                  run date. Leave Whole Month on to include every day in the
                  selected month.
                </SectionDescription>
              </SectionHeader>
              <ActivationRow>
                <Text variant='h5'>Whole Month</Text>
                <SwitchToggleV2
                  active={form.wholeMonth}
                  onClick={() => updateForm('wholeMonth', !form.wholeMonth)}
                  size='large'
                />
              </ActivationRow>
              {form.wholeMonth ? (
                <Field>
                  <Label variant='action2'>Month*</Label>
                  <Dropdown
                    items={scheduledReportMonthItems}
                    active={form.reportStartMonth}
                    itemCallback={(item: SelectItemProps) => {
                      const month = item.id as ScheduledReportMonth
                      setForm((currentForm) => ({
                        ...currentForm,
                        reportStartMonth: month,
                        reportEndMonth: month,
                      }))
                    }}
                    width='100%'
                  />
                </Field>
              ) : (
                <>
                  <Field>
                    <Label variant='action2'>Start Month*</Label>
                    <Dropdown
                      items={scheduledReportMonthItems}
                      active={form.reportStartMonth}
                      itemCallback={(item: SelectItemProps) =>
                        updateForm(
                          'reportStartMonth',
                          item.id as ScheduledReportMonth
                        )
                      }
                      width='100%'
                    />
                  </Field>
                  <Field>
                    <Label variant='action2'>End Month*</Label>
                    <Dropdown
                      items={scheduledReportMonthItems}
                      active={form.reportEndMonth}
                      itemCallback={(item: SelectItemProps) =>
                        updateForm(
                          'reportEndMonth',
                          item.id as ScheduledReportMonth
                        )
                      }
                      width='100%'
                    />
                  </Field>
                  <RangeRow>
                    <TextField
                      label='Start Day'
                      type='number'
                      placeholder='1'
                      value={form.reportStartDay}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateForm('reportStartDay', event.currentTarget.value)
                      }
                      invalid={showStartDayInvalid}
                      helpText='Enter a day from 1 to 31.'
                      width='100%'
                    />
                    <TextField
                      label='End Day'
                      type='number'
                      placeholder='31'
                      value={form.reportEndDay}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateForm('reportEndDay', event.currentTarget.value)
                      }
                      invalid={showEndDayInvalid}
                      helpText='Enter a day from 1 to 31.'
                      width='100%'
                    />
                  </RangeRow>
                </>
              )}
              {showRangeInvalid && (
                <HelpText variant='body2'>
                  Report end must be on or after report start.
                </HelpText>
              )}
            </Section>
            <TextField
              label='Recipients*'
              placeholder='email@example.com, another@example.com'
              value={form.recipients}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                updateForm('recipients', event.currentTarget.value)
              }
              invalid={areRecipientsInvalid}
              helpText='Enter at least one recipient email.'
              width='100%'
            />
            <Field>
              <Label variant='action2'>Groups</Label>
              <WorksiteGroupDropdown
                items={getScheduledReportSegmentItems(allSegments)}
                activeItems={selectedSegments}
                onItemCallback={setSelectedSegments}
                width='100%'
                showAllSelectedItems={false}
              />
            </Field>
            {isEditing && (
              <Field>
                <Label variant='action2'>Status</Label>
                <ActivationRow>
                  <Text variant='body1'>
                    {form.isActive ? 'Active' : 'Inactive'}
                  </Text>
                  <SwitchToggleV2
                    active={form.isActive}
                    onClick={() => updateForm('isActive', !form.isActive)}
                    size='large'
                  />
                </ActivationRow>
              </Field>
            )}
            {saveError && (
              <HelpText variant='body2'>{saveError}</HelpText>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <FooterContent>
            <SecondaryButton onClick={closeModal}>Cancel</SecondaryButton>
            <PrimaryButton
              onClick={submit}
              disabled={isSubmitDisabled && submitted}
              isLoading={isSaving}
            >
              {isEditing ? 'Update' : 'Create'}
            </PrimaryButton>
          </FooterContent>
        </ModalFooter>
      </Modal>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  ScheduledReportCreateModal.displayName = 'ScheduledReportCreateModal'
}

export default ScheduledReportCreateModal
